import { QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { browserNotificationRuntime } from '@/lib/browser-notification-runtime'
import type { BrowserNotificationRuntime } from '@/lib/browser-notification-runtime'
import { useTRPC } from '@/lib/trpc'
import {
  adoptWebPushSubscriptionActivation,
  captureWebPushInstallationBinding,
  clearWebPushInstallationId,
  invalidateWebPushSubscriptionAdoption,
  WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY,
  webPushInstallationBindingIsCurrent,
} from '@/lib/web-push-client'
import { reconcileWebPushIdentity } from '@/lib/web-push-reconciliation'

const EMPTY_INSTALLATION_ID = '00000000-0000-4000-8000-000000000000'

export function useWebPushStartupReconciliation(
  expectedUserId: string | null,
  expectedSessionId: string | null,
  enabled: boolean,
  runtime: BrowserNotificationRuntime = browserNotificationRuntime,
): void {
  const trpc = useTRPC()
  const [storageRevision, setStorageRevision] = useState(0)
  const observedBinding = captureWebPushInstallationBinding()
  const {
    installationId,
    ownerUserId,
    ownerSessionId,
    bindingToken,
    identityRevision,
  } = observedBinding
  const ownerMatches
    = !!expectedUserId
      && !!expectedSessionId
      && ownerUserId === expectedUserId
      && ownerSessionId === expectedSessionId
      && !!bindingToken
  const canReadServer = enabled && ownerMatches && !!installationId
  const status = useQuery({
    ...trpc.webPushRouter.status.queryOptions({
      expectedUserId: expectedUserId ?? '',
      expectedSessionId: expectedSessionId ?? '',
      installationId: installationId ?? EMPTY_INSTALLATION_ID,
    }),
    enabled: canReadServer,
    retry: 1,
    refetchOnWindowFocus: true,
  })
  const refreshMutation = useMutation(
    trpc.webPushRouter.refresh.mutationOptions(),
  )
  const deactivateMutation = useMutation(
    trpc.webPushRouter.deactivate.mutationOptions(),
  )
  const refreshSubscriptionRef = useRef(refreshMutation.mutateAsync)
  const deactivateSubscriptionRef = useRef(deactivateMutation.mutateAsync)
  refreshSubscriptionRef.current = refreshMutation.mutateAsync
  deactivateSubscriptionRef.current = deactivateMutation.mutateAsync
  const lastAttempt = useRef<string | null>(null)
  const serverState = status.data?.subscriptionState
  const serviceAvailable = status.data?.serviceAvailable
  const refetchStatus = status.refetch
  const permission = runtime.notificationPermission()
  const lifecycleLockAvailable = runtime.readCapabilities().lifecycleLock

  useEffect(() => {
    const observeSiblingLifecycle = (event: StorageEvent) => {
      if (event.key === WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY)
        setStorageRevision(value => value + 1)
    }
    window.addEventListener('storage', observeSiblingLifecycle)
    return () => window.removeEventListener('storage', observeSiblingLifecycle)
  }, [])

  useEffect(() => {
    if (storageRevision > 0 && canReadServer)
      void refetchStatus()
  }, [canReadServer, refetchStatus, storageRevision])

  useEffect(() => {
    if (!enabled || !lifecycleLockAvailable)
      return
    if (canReadServer && (status.isFetching || status.isError || !serverState))
      return

    const key = [
      expectedUserId ?? 'signed-out',
      expectedSessionId ?? 'no-session',
      ownerUserId ?? 'no-owner',
      ownerSessionId ?? 'no-owner-session',
      installationId ?? 'no-installation',
      bindingToken ?? 'no-binding',
      identityRevision ?? 'no-revision',
      permission,
      serverState ?? 'no-server-row',
      serviceAvailable ? 'available' : 'unavailable',
    ].join(':')
    if (lastAttempt.current === key)
      return
    lastAttempt.current = key

    let cancelled = false
    let lifecycleBinding = captureWebPushInstallationBinding()
    const startedWithObservedIdentity
      = lifecycleBinding.identityRevision === identityRevision
        && lifecycleBinding.installationId === installationId
        && lifecycleBinding.ownerUserId === ownerUserId
        && lifecycleBinding.ownerSessionId === ownerSessionId
        && lifecycleBinding.bindingToken === bindingToken
    const identityIsCurrent = () =>
      !cancelled
      && startedWithObservedIdentity
      && webPushInstallationBindingIsCurrent(lifecycleBinding)
    const commitLifecycleBinding = (
      next: typeof lifecycleBinding,
    ): void => {
      lifecycleBinding = next
    }
    const invalidateSubscriptionAdoption = () => {
      if (!identityIsCurrent())
        return false
      const next = invalidateWebPushSubscriptionAdoption(lifecycleBinding)
      if (!next)
        return false
      commitLifecycleBinding(next)
      return true
    }
    const adoptSubscription = async (subscription: PushSubscription) => {
      if (!identityIsCurrent())
        return false
      if (!lifecycleBinding.subscriptionGeneration)
        return true
      const next = await adoptWebPushSubscriptionActivation(
        lifecycleBinding,
        subscription.endpoint,
      )
      if (!next)
        return false
      commitLifecycleBinding(next)
      return true
    }
    void runtime
      .withWebPushLifecycleLock(async () => {
        if (!identityIsCurrent())
          return
        let authoritativeServerState = serverState
        let authoritativeServiceAvailable = serviceAvailable
        if (
          ownerMatches
          && expectedUserId
          && expectedSessionId
          && installationId
        ) {
          const freshStatus = await (async () => {
            const probeQueryClient = new QueryClient({
              defaultOptions: { queries: { retry: false } },
            })
            try {
              return await probeQueryClient.fetchQuery(
                trpc.webPushRouter.status.queryOptions({
                  expectedUserId,
                  expectedSessionId,
                  installationId,
                }),
              )
            }
            finally {
              probeQueryClient.clear()
            }
          })()
          if (!identityIsCurrent())
            return
          authoritativeServerState = freshStatus.subscriptionState
          authoritativeServiceAvailable = freshStatus.serviceAvailable
        }
        return reconcileWebPushIdentity({
          expectedUserId: ownerMatches ? expectedUserId : null,
          expectedSessionId: ownerMatches ? expectedSessionId : null,
          installationId,
          bindingToken: ownerMatches ? bindingToken : null,
          permission: runtime.notificationPermission(),
          serverState: authoritativeServerState,
          serviceAvailable: authoritativeServiceAvailable,
          runtime,
          client: {
            refresh: input => refreshSubscriptionRef.current(input),
            deactivate: input => deactivateSubscriptionRef.current(input),
          },
          clearInstallationId: clearWebPushInstallationId,
          adoptSubscription,
          invalidateSubscriptionAdoption,
          isCurrent: identityIsCurrent,
        })
      })
      .catch(() => {
        lastAttempt.current = null
      })
    return () => {
      cancelled = true
    }
  }, [
    canReadServer,
    enabled,
    expectedSessionId,
    expectedUserId,
    bindingToken,
    identityRevision,
    installationId,
    lifecycleLockAvailable,
    ownerMatches,
    ownerUserId,
    ownerSessionId,
    permission,
    runtime,
    serverState,
    serviceAvailable,
    status.isError,
    status.isFetching,
    trpc.webPushRouter.status,
  ])
}
